import React, { useState, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import Chart from 'react-apexcharts';

import { useGetDistributorSubscribersDataQuery, useGetInvestmentFundsQuery } from 'api/main';
import { subscriptionStatus } from 'utils/constants';
import { parseSum } from 'utils/helpers';
import { texts } from 'utils/languages/fr';
import {
    customTotalParser,
    parseCustomData,
    pieChartOptions,
    subscriptionNumberFormatter,
} from '../admin-distributors/AdminDistributorDashboard';

import { InfoCard, DashboardSection, ChartHeader, ChartContainer } from '../common-components';
import SubscriptionsByPartner from './SubscriptionsByPartner';
import { CustomSelect, Loader } from 'components';
import { Typography, Box, MenuItem, Grid } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    filterInput: {
        width: 250,
        marginBottom: theme.spacing(3),
    },
    whiteBackground: {
        background: theme.palette.common.white,
    },
    infoContainer: {
        marginBottom: theme.spacing(2),
        paddingBottom: theme.spacing(3),
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    subtitle: {
        paddingLeft: theme.spacing(1),
        fontWeight: 600,
    },
    subsection: {
        paddingLeft: theme.spacing(3),
    },
}));

const numberFormatter = val => parseSum(val);

const chartTooltipOptions = formatter => ({
    ...pieChartOptions.tooltip,
    y: { formatter },
});

const InProgressSection = props => {
    const { onListFilter, userIsNetworkAdmin, userIsProvider, userIsServicer } = props;
    const classes = useStyles();

    const { data: fundList = [], isFetching: fundsLoader } = useGetInvestmentFundsQuery({
        visible: 1,
    });
    const { data: aggregatedDistributorData = {}, isFetching: dataLoader } =
        useGetDistributorSubscribersDataQuery({
            subscriptionStatus: subscriptionStatus.IN_PROGRESS,
        });

    const [selectedFund, setSelectedFund] = useState('all');

    const aggregatedData = aggregatedDistributorData;

    // used for charts that show data by fund
    const filteredFundList = useMemo(
        () => fundList.filter(f => !!aggregatedData?.[f.id]),
        [fundList, aggregatedData]
    );
    const nrSubscriptionsPendingCifValidation = useMemo(
        () => aggregatedData?.nr_subscriptions_pending_cif_validation,
        [aggregatedData]
    );
    const totalValueByFund = useMemo(
        () => customTotalParser(aggregatedData, selectedFund, 'sum_total_subscriptions'),
        [aggregatedData, selectedFund]
    );
    const totalNumberByFund = useMemo(
        () => customTotalParser(aggregatedData, selectedFund, 'total_subscriptions'),
        [aggregatedData, selectedFund]
    );

    const chartLabels = useMemo(
        () =>
            selectedFund === 'all'
                ? filteredFundList.map(f => f.name)
                : Object.keys(aggregatedData?.[selectedFund] || {}).map(k => k || '-'),
        [selectedFund, filteredFundList, aggregatedData]
    );

    return (
        <DashboardSection title={texts.ONGOING}>
            <Loader loading={dataLoader || fundsLoader || !aggregatedData} marginTop={10}>
                {!userIsProvider && (
                    <Box className={classes.infoContainer}>
                        <Typography variant="body1" gutterBottom className={classes.subtitle}>
                            {texts.PARTNERS}
                        </Typography>
                        <Grid container spacing={2}>
                            <InfoCard
                                onClick={onListFilter(
                                    { distributorSubscriptionInProgress: 1 },
                                    'distributors'
                                )}
                                number={aggregatedData?.distributors_unfinished_subscriptions}
                                label={texts.UNFINISHED_INSCRIPTIONS}
                            />
                            {!userIsServicer && (
                                <InfoCard
                                    onClick={onListFilter(
                                        { pendingCIFValidation: 1 },
                                        'subscriptions'
                                    )}
                                    number={nrSubscriptionsPendingCifValidation}
                                    label={texts.SUBSCRIPTIONS_TO_VALIDATE}
                                />
                            )}
                        </Grid>
                    </Box>
                )}
                <Box className={classes.infoContainer}>
                    <Typography variant="body1" gutterBottom className={classes.subtitle}>
                        {texts.SUBSCRIPTIONS}
                    </Typography>
                    <Box className={classes.subsection}>
                        <CustomSelect
                            inputStyle={classes.filterInput}
                            classes={{ root: classes.whiteBackground }}
                            value={selectedFund}
                            onChange={e => setSelectedFund(e.target.value)}
                            label={texts.PRODUIT}
                        >
                            <MenuItem value="all">{texts.ALL_1}</MenuItem>
                            {filteredFundList
                                .sort((a, b) => (a.name > b.name ? 1 : -1))
                                .map(f => (
                                    <MenuItem key={f.id} value={f.id}>
                                        {f.name}
                                    </MenuItem>
                                ))}
                        </CustomSelect>
                        {!userIsNetworkAdmin && (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-around"
                                marginBottom={3}
                            >
                                <ChartHeader
                                    title={texts.VALUE}
                                    number={totalValueByFund}
                                    handleClick={onListFilter(
                                        {
                                            product:
                                                selectedFund === 'all' ? undefined : [selectedFund],
                                        },
                                        'subscriptions'
                                    )}
                                    useSymbol
                                />
                                <ChartHeader
                                    title={texts.NUMBER}
                                    number={totalNumberByFund}
                                    handleClick={onListFilter(
                                        {
                                            product:
                                                selectedFund === 'all' ? undefined : [selectedFund],
                                        },
                                        'subscriptions'
                                    )}
                                />
                            </Box>
                        )}
                        {totalValueByFund === 0 && totalNumberByFund === 0 ? (
                            <Typography variant="subtitle1" align="center" gutterBottom>
                                {texts.NO_DATA_AVAILABLE}
                            </Typography>
                        ) : (
                            <Box display="flex" alignItems="center" gap="15px" marginBottom={3}>
                                {!userIsNetworkAdmin && (
                                    <ChartContainer
                                        subtitleProps={{
                                            selectedFund: selectedFund,
                                            subType: 'direct',
                                            valueType: 'value',
                                        }}
                                        flex="0 1 25%"
                                    >
                                        <Chart
                                            type="pie"
                                            series={parseCustomData(
                                                aggregatedData,
                                                selectedFund,
                                                'sum_direct_subscriptions'
                                            )}
                                            options={{
                                                ...pieChartOptions,
                                                tooltip: chartTooltipOptions(numberFormatter),
                                                labels: chartLabels,
                                            }}
                                        />
                                    </ChartContainer>
                                )}
                                <ChartContainer
                                    title={userIsNetworkAdmin ? texts.VALUE : ''}
                                    number={totalValueByFund}
                                    handleClick={onListFilter(
                                        {
                                            product:
                                                selectedFund === 'all' ? undefined : [selectedFund],
                                        },
                                        'subscriptions'
                                    )}
                                    useSymbol
                                    subtitleProps={{
                                        selectedFund: selectedFund,
                                        subType: 'indirect',
                                        valueType: 'value',
                                    }}
                                    display="flex"
                                    flexDirection="column"
                                    flex={`0 1 ${userIsNetworkAdmin ? 40 : 25}%`}
                                >
                                    <Chart
                                        type="pie"
                                        series={parseCustomData(
                                            aggregatedData,
                                            selectedFund,
                                            'sum_indirect_subscriptions'
                                        )}
                                        options={{
                                            ...pieChartOptions,
                                            tooltip: chartTooltipOptions(numberFormatter),
                                            labels: chartLabels,
                                        }}
                                    />
                                </ChartContainer>
                                {!userIsNetworkAdmin && (
                                    <ChartContainer
                                        subtitleProps={{
                                            selectedFund: selectedFund,
                                            subType: 'direct',
                                            valueType: 'number',
                                        }}
                                        flex="0 1 25%"
                                    >
                                        <Chart
                                            type="pie"
                                            series={parseCustomData(
                                                aggregatedData,
                                                selectedFund,
                                                'direct_subscriptions'
                                            )}
                                            options={{
                                                ...pieChartOptions,
                                                tooltip: chartTooltipOptions(
                                                    subscriptionNumberFormatter
                                                ),
                                                labels: chartLabels,
                                            }}
                                        />
                                    </ChartContainer>
                                )}
                                <ChartContainer
                                    subtitleProps={{
                                        selectedFund: selectedFund,
                                        subType: 'indirect',
                                        valueType: 'number',
                                    }}
                                    title={userIsNetworkAdmin ? texts.NUMBER : ''}
                                    number={totalNumberByFund}
                                    handleClick={onListFilter(
                                        {
                                            product:
                                                selectedFund === 'all' ? undefined : [selectedFund],
                                        },
                                        'subscriptions'
                                    )}
                                    flex={`0 1 ${userIsNetworkAdmin ? 40 : 25}%`}
                                >
                                    <Chart
                                        type="pie"
                                        series={parseCustomData(
                                            aggregatedData,
                                            selectedFund,
                                            'indirect_subscriptions'
                                        )}
                                        options={{
                                            ...pieChartOptions,
                                            tooltip: chartTooltipOptions(
                                                subscriptionNumberFormatter
                                            ),
                                            labels: chartLabels,
                                        }}
                                    />
                                </ChartContainer>
                            </Box>
                        )}
                    </Box>
                </Box>
                <SubscriptionsByPartner
                    status={subscriptionStatus.IN_PROGRESS}
                    classes={classes}
                    onListFilter={onListFilter}
                    type="ongoing"
                />
                {!userIsProvider && (
                    <InfoCard
                        onClick={onListFilter({ pendingTransfer: 1 }, 'subscriptions')}
                        number={aggregatedData?.indirect_subscriptions_pending_transfer}
                        label={texts.INDIRECT_SUBSCRIPTIONS_PENDING_TRANSFER}
                    />
                )}
            </Loader>
        </DashboardSection>
    );
};

InProgressSection.propTypes = {
    onListFilter: PropTypes.func.isRequired,
    userIsNetworkAdmin: PropTypes.bool,
    userIsProvider: PropTypes.bool,
    userIsServicer: PropTypes.bool,
};

export default InProgressSection;
